import { apiClient } from './client'
import type { ApiResponse } from './client'

export interface CompanyLookupViewDto {
  Id: string
  LinkedInId: string
  LastEnrichedUtc?: string | null // ISO8601
  NeedsEnrichment?: boolean | null
  Name?: string | null
  NormalizedName?: string | null
  UniversalName?: string | null
  LinkedInUrl?: string | null
  Tagline?: string | null
  Description?: string | null
  Type?: string | null
  Phone?: string | null
  StaffCount?: number | null
  StaffCountRange?: string | null
  FollowerCount?: number | null
  Website?: string | null
  FoundedYear?: number | null
  CrunchbaseUrl?: string | null
  PageVerification?: string | null
  Logo?: string | null
  Cover?: string | null
  Industries?: string[] | null
  Specialities?: string[] | null
}

export interface CompanySearchResultDto {
  Id: string // LinkedIn company identifier
  Name?: string | null
  NormalizedName?: string | null
  LinkedInUrl?: string | null
  Website?: string | null
  Logo?: string | null
  Industries?: string[] | null
  StaffCountRange?: string | null
}

export interface CompanyBrandDto {
  CompanyId: string
  Name?: string | null
  LogoUrl?: string | null
  Website?: string | null
  ValueProposition?: string | null
  BrandColors?: string[] | null
  BrandFontPrimary?: string | null
  BrandFontSecondary?: string | null
  BrandVoiceTone?: string | null
  BrandTagline?: string | null
  MissionStatement?: string | null
  CoreValues?: string[] | null
  BrandColorPrimary?: string | null
  BrandColorSecondary?: string | null
  BrandColorAccent?: string | null
  BrandColorBackground?: string | null
  BrandColorText?: string | null
  FaviconUrl?: string | null
  OgImageUrl?: string | null
  WebsiteLastScrapedUtc?: string | null
  // Persisted onto the company document by ORB-609; the API has returned them since 2026-08-12.
  ProductsAndServices?: string[] | null
  Summary?: string | null
  Keywords?: string[] | null
}

/**
 * One targeted company as a single workspace knows it (ORB-623).
 *
 * The facts come from the global company document, which every tenant shares. `People` and
 * `Measures` come only from this workspace's own leads and audience rows — never the company's
 * full roster.
 */
export interface CompanyProfileDto {
  CompanyId: string
  WorkspaceId: string
  ClientCompanyId: string
  /** False when no company document backs this id — render from what's here and say detail is limited. */
  HasCompanyRecord: boolean

  Name?: string | null
  /** Absolute URL, already composed server-side. Never a raw blob path. */
  LogoUrl?: string | null
  Website?: string | null
  LinkedInUrl?: string | null
  Tagline?: string | null

  // The list fields below are optional on purpose: a field the server omits arrives here as
  // undefined whatever the C# initializer says, and typing them as always-present made every
  // `.length` a crash the compiler happily blessed.
  PrimaryIndustry?: string | null
  Industries?: string[] | null
  StaffCountRange?: string | null
  StaffCount?: number | null
  Headquarters?: string | null
  FoundedYear?: number | null
  FollowerCount?: number | null
  Specialities?: string[] | null
  EmailDomains?: string[] | null

  Summary?: string | null
  ProductsAndServices?: string[] | null
  ValueProposition?: string | null
  MissionStatement?: string | null
  CoreValues?: string[] | null
  Keywords?: string[] | null
  WebsiteLastScrapedUtc?: string | null
  LastEnrichedUtc?: string | null

  Measures: CompanyProfileMeasuresDto
  /** Null when there's nothing to report — no CRM connected, or no deal matched. Never a zero. */
  Pipeline?: CompanyProfilePipelineDto | null
  People: CompanyProfilePersonDto[]
  GeneratedAtUtc: string
}

export interface CompanyProfilePipelineDto {
  PipelineGeneratedTotal: number
  PipelineGeneratedCount: number
  ClosedWonTotal: number
  ClosedWonCount: number
  /** Null when the deals behind these totals don't all share one currency. */
  Currency?: string | null
  /** Only the domains that actually carried deals — what confirming will attribute, tenant-wide. */
  MatchedDomains: string[]
  /** False means the figures came from a domain match alone — offer to confirm the link. */
  IsLinked: boolean
  LinkSource?: 'Auto' | 'Confirmed' | null
}

export interface CompanyProfileMeasuresDto {
  PeopleCount: number
  /** The same set the dashboard's Conversions metric counts, so the two reconcile. */
  ConvertedCount: number
  SignalScore: number
  FirstTargetedUtc?: string | null
  LastConversionUtc?: string | null
  CampaignCount: number
  NonProspectCount: number
}

/** "Targeted" — on an uploaded list. "Converted" — a lead exists, so they registered or converted. */
export type CompanyProfilePersonStatus = 'Targeted' | 'Converted'

export interface CompanyProfilePersonDto {
  /** Which owned record this row opens — matches ContactDetailDto's anchor vocabulary. */
  AnchorType: 'Lead' | 'Audience'
  AnchorId: string
  ContactId?: string | null
  FullName?: string | null
  JobTitle?: string | null
  Email?: string | null
  ProfilePicUrl?: string | null
  Status: CompanyProfilePersonStatus
  SignalScore: number
  SignalLevel?: string | null
  CampaignCount: number
  LastActivityUtc?: string | null
  IsNonProspect: boolean
}

export interface CompanyEnrichedDto {
  Id: string
  LinkedInId: string
  Name?: string | null
  NormalizedName?: string | null
  LinkedInUrl?: string | null
  Description?: string | null
  Industries?: string[] | null
  StaffCountRange?: string | null
  Website?: string | null
  Logo?: string | null
  FollowerCount?: number | null
}

export const companiesApi = {
  getBrand: (companyId: string): Promise<ApiResponse<CompanyBrandDto>> =>
    apiClient.get(`/api/companies/${companyId}/brand`),

  /**
   * Point this workspace's CRM Named Accounts for these domains at this company (ORB-623).
   * Scoped to the workspace, and never touches the shared global company record.
   */
  confirmCrmLink: (
    clientCompanyId: string,
    workspaceId: string,
    companyId: string,
    domains: string[]
  ): Promise<ApiResponse<{ domains: string[]; companyId: string; updated: number }>> =>
    apiClient.post(
      `/api/ClientCompanies/${encodeURIComponent(clientCompanyId)}/Workspaces/${encodeURIComponent(workspaceId)}/Companies/${encodeURIComponent(companyId)}/CrmLink`,
      { Domains: domains }
    ),

  /**
   * One targeted company as this workspace knows it (ORB-623).
   * 404 when the workspace has no record of the company — that is the tenancy boundary, so a
   * caller should render "no record of this company", not an empty profile.
   */
  getWorkspaceProfile: (
    clientCompanyId: string,
    workspaceId: string,
    companyId: string,
    signal?: AbortSignal
  ): Promise<ApiResponse<CompanyProfileDto>> =>
    apiClient.get(
      `/api/ClientCompanies/${encodeURIComponent(clientCompanyId)}/Workspaces/${encodeURIComponent(workspaceId)}/Companies/${encodeURIComponent(companyId)}`,
      { signal }
    ),


  search: (searchTerm: string, maxResults = 25, signal?: AbortSignal): Promise<ApiResponse<CompanySearchResultDto[]>> => {
    if (searchTerm.length < 3) {
      const resp: ApiResponse<CompanySearchResultDto[]> = { data: [], status: 200, statusText: 'OK' }
      return Promise.resolve(resp)
    }
    const qs = `searchTerm=${encodeURIComponent(searchTerm)}&maxResults=${maxResults}`
    return apiClient.get(`/api/companies/search?${qs}`, { signal }) as Promise<ApiResponse<CompanySearchResultDto[]>>
  },
  
  enrichByLinkedInId: (linkedInIdOrUrl: string): Promise<ApiResponse<CompanyEnrichedDto>> => {
    const trimmed = linkedInIdOrUrl.trim()
    
    // Check if it's a URL (contains linkedin.com/company/)
    if (trimmed.includes('linkedin.com/company/')) {
      const qs = `linkedInCompanyUrl=${encodeURIComponent(trimmed)}`
      return apiClient.post(`/api/companies/enrich?${qs}`, {})
    }
    
    // Otherwise treat as LinkedIn company ID
    const qs = `linkedInCompanyId=${encodeURIComponent(trimmed)}`
    return apiClient.post(`/api/companies/enrich?${qs}`, {})
  }
}
